import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getVisit, saveVisit } from '../utils/api';
import { PRODUCTS, RESULT, time12, dayLabel, telLink, mapLink, money, fmtPhone } from '../utils/leads';

/* The rep's appointment page — opened from the link in their text, no login.
   Shows the appointment with Call / Directions buttons, and lets them log the result. */

const CSS = `
.vz{min-height:100vh;background:#EEF2EF;font-family:'Inter',system-ui,sans-serif;color:#13211B;padding:0 0 40px}
.vz-top{background:#0D3B2E;color:#fff;padding:22px 20px 26px}
.vz-top small{font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:#F0C842;font-weight:800}
.vz-top h1{font-family:'Fraunces',Georgia,serif;font-size:28px;margin:6px 0 4px;line-height:1.15}
.vz-top p{margin:0;font-size:15px;color:rgba(255,255,255,.85)}
.vz-body{max-width:560px;margin:-14px auto 0;padding:0 14px;display:flex;flex-direction:column;gap:12px}
.vz-card{background:#fff;border-radius:16px;padding:16px 18px;box-shadow:0 1px 2px rgba(0,0,0,.05)}
.vz-card h2{font-size:13px;letter-spacing:.08em;text-transform:uppercase;color:#5E6E66;margin:0 0 10px}
.vz-big{font-size:20px;font-weight:800}
.vz-acts{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:14px}
.vz-acts a{display:flex;align-items:center;justify-content:center;gap:8px;text-decoration:none;font-weight:800;font-size:15px;border-radius:12px;padding:13px;background:#0D3B2E;color:#fff}
.vz-acts a.alt{background:#EEF2EF;color:#0D3B2E}
.vz-row{display:flex;justify-content:space-between;gap:12px;padding:8px 0;border-top:1px solid #EEF2EF;font-size:15px}
.vz-row:first-of-type{border-top:0}
.vz-row span:first-child{color:#5E6E66}
.vz-row span:last-child{font-weight:700;text-align:right}
.vz-note{background:#FFF8E6;border-radius:10px;padding:10px 12px;font-size:15px;line-height:1.5;margin-top:10px}
.vz-res{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.vz-res button{font:inherit;font-size:15px;font-weight:800;border:2px solid #D9E2DC;background:#fff;border-radius:12px;padding:14px 8px;cursor:pointer;color:#13211B}
.vz-res button.sold{grid-column:1 / -1;font-size:18px;padding:16px}
.vz-res button.on{color:#fff}
.vz-in{font:inherit;font-size:17px;padding:12px 14px;border:2px solid #D9E2DC;border-radius:12px;width:100%;box-sizing:border-box;background:#fff;color:#13211B}
.vz-in:focus{outline:none;border-color:#0D3B2E}
.vz-chips{display:flex;flex-wrap:wrap;gap:6px}
.vz-chips button{font:inherit;font-size:14px;font-weight:700;border:2px solid #D9E2DC;background:#fff;border-radius:999px;padding:7px 12px;cursor:pointer}
.vz-chips button.on{background:#0D3B2E;border-color:#0D3B2E;color:#fff}
.vz-go{font:inherit;font-size:18px;font-weight:800;border:0;border-radius:14px;padding:16px;background:#F0C842;color:#13211B;cursor:pointer;width:100%}
.vz-go:disabled{opacity:.5}
.vz-err{color:#A3322A;font-weight:700;font-size:14px}
.vz-done{text-align:center;padding:26px 18px}
.vz-done b{display:block;font-family:'Fraunces',Georgia,serif;font-size:26px;margin-bottom:6px}
label.vz-l{display:block;font-size:13px;font-weight:700;color:#5E6E66;margin:12px 0 6px}
`;

export default function Visit() {
  const { token } = useParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState('');
  const [disp, setDisp] = useState('');
  const [amount, setAmount] = useState('');
  const [products, setProducts] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getVisit(token).then(d => { setV(d); setDisp(d.disp && d.results[d.disp] ? d.disp : ''); setAmount(d.amount ? String(d.amount) : ''); setProducts(d.products || []); setNotes(d.result_notes || ''); })
      .catch(x => setErr(x.response?.data?.error || 'Could not open this appointment. Check your signal and try again.'));
  }, [token]);

  async function submit() {
    setMsg('');
    if (!disp) return setMsg('Pick a result first');
    if (disp === '*SOLD*' && !(parseFloat(String(amount).replace(/[$,]/g, '')) > 0)) return setMsg('Enter the sale amount');
    setSaving(true);
    try { await saveVisit(token, { disp, amount, notes, products: disp === '*SOLD*' ? products : undefined }); setDone(true); window.scrollTo({ top: 0 }); }
    catch (x) { setMsg(x.response?.data?.error || 'Could not save. Check your signal and try again.'); }
    finally { setSaving(false); }
  }

  if (err) return <div className="vz"><style>{CSS}</style><div className="vz-top"><small>CPR Home Solutions</small><h1>Appointment</h1></div><div className="vz-body"><div className="vz-card vz-done"><b>Can't open this</b>{err}</div></div></div>;
  if (!v) return <div className="vz"><style>{CSS}</style><div className="vz-top"><small>CPR Home Solutions</small><h1>Loading…</h1></div></div>;

  return (
    <div className="vz">
      <style>{CSS}</style>
      <div className="vz-top">
        <small>CPR Home Solutions · {v.rep}</small>
        <h1>{v.customer}{v.customer_2 ? ` & ${v.customer_2}` : ''}</h1>
        <p>{dayLabel(v.appt_date)} · <b>{time12(v.appt_time)}</b></p>
      </div>
      <div className="vz-body">
        {done ? (
          <div className="vz-card vz-done"><b>{disp === '*SOLD*' ? '🎉 Nice sale!' : 'Result saved'}</b>
            {RESULT[disp]?.label}{disp === '*SOLD*' ? ` · ${money(String(amount).replace(/[$,]/g, ''))}` : ''}. The office can see it now.
            <div style={{ marginTop: 14 }}><button type="button" className="vz-go" style={{ background: '#EEF2EF' }} onClick={() => setDone(false)}>Change it</button></div></div>
        ) : null}

        <div className="vz-card">
          <h2>Where</h2>
          <div className="vz-big">{v.address}</div>
          <div style={{ color: '#5E6E66' }}>{[v.city, v.zip].filter(Boolean).join(' ')}</div>
          <div className="vz-acts">
            <a href={mapLink(v)} target="_blank" rel="noreferrer">📍 Directions</a>
            <a className="alt" href={telLink(v.phone)}>📞 Call</a>
          </div>
        </div>

        <div className="vz-card">
          <h2>Appointment</h2>
          <div className="vz-row"><span>Phone</span><span>{fmtPhone(v.phone)}{v.phone_2 ? ` · ${fmtPhone(v.phone_2)}` : ''}</span></div>
          <div className="vz-row"><span>Meet with</span><span>{v.meet_with || '—'}</span></div>
          <div className="vz-row"><span>Wants</span><span>{v.products.join(', ') || '—'}</span></div>
          <div className="vz-row"><span>Type</span><span>{v.appt_type}</span></div>
          {v.partners.length > 0 && <div className="vz-row"><span>With</span><span>{v.partners.join(', ')}</span></div>}
          <div className="vz-row"><span>Set by</span><span>{v.set_by || '—'}{v.crew ? ` · ${v.crew}` : ''}</span></div>
          {v.notes && <div className="vz-note">{v.notes}</div>}
        </div>

        {!done && (
          <div className="vz-card">
            <h2>How did it go?</h2>
            <div className="vz-res">{Object.entries(v.results).map(([k, label]) => (
              <button key={k} type="button" className={(k === '*SOLD*' ? 'sold ' : '') + (disp === k ? 'on' : '')} style={disp === k ? { background: RESULT[k].color, borderColor: RESULT[k].color, color: RESULT[k].fg } : undefined} onClick={() => setDisp(k)}>{k === '*SOLD*' ? '💰 ' : ''}{label}</button>))}</div>
            {disp === '*SOLD*' && <>
              <label className="vz-l" htmlFor="vz-amt">Sale amount</label>
              <input id="vz-amt" className="vz-in" inputMode="decimal" placeholder="$24,500" value={amount} onChange={e => setAmount(e.target.value)} />
              <label className="vz-l">What they bought</label>
              <div className="vz-chips">{PRODUCTS.map(p => <button key={p} type="button" className={products.includes(p) ? 'on' : ''} onClick={() => setProducts(x => (x.includes(p) ? x.filter(y => y !== p) : [...x, p]))}>{p}</button>)}</div>
            </>}
            <label className="vz-l" htmlFor="vz-notes">Notes for the office</label>
            <textarea id="vz-notes" className="vz-in" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder={disp === '*SOLD*' ? 'Financing, what was included…' : 'What happened? Call back date?'} />
            {msg && <div className="vz-err" style={{ marginTop: 10 }}>{msg}</div>}
            <button type="button" className="vz-go" style={{ marginTop: 14 }} disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Save result'}</button>
          </div>
        )}
      </div>
    </div>
  );
}
